import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import {
    findDirectChildCategory,
    hasPlpSubcategoryTags,
    plpHeaderDisplayForCategoryNode,
} from '@/components/eataly-category-plp-header/plp-header-display';
import { resolveCategoryNodeForPlpPathname } from '@/lib/plp/resolve-category-for-pathname';

// Temporary experiment: keep the current server-rendered header during PLP navigation.
const PLP_HEADER_OPTIMISM_ENABLED = false;

export type PlpHeaderOptimisticState = {
    /** Category node used for title, description, and subcategory link resolution. */
    displayCategory: ShopperProducts.schemas['Category'];
    title: string;
    description: string;
    showDescriptionSkeleton: boolean;
    showSubcategoriesSkeleton: boolean;
    /** True while PLP navigation is in flight (refine/page/sort or category→category). */
    isNavigating: boolean;
    /** Tag link click (direct child or sibling) before the route loader resolves the target category. */
    setPendingNavigationTarget: (target: { id: string; name: string }) => void;
};

/**
 * Derives optimistic PLP header copy and skeleton flags while category→category navigation is pending.
 */
export function usePlpHeaderOptimisticState(
    category: ShopperProducts.schemas['Category'],
    isNavigating: boolean
): PlpHeaderOptimisticState {
    const location = useLocation();
    const navigation = useNavigation();

    // Local state to track immediate clicks on subcategory tags before the router registers the transition
    const [pendingNavigationTarget, setPendingNavigationTarget] = useState<{ id: string; name: string } | null>(null);

    // Reset the local optimistic click state as soon as the router finishes loading the new route
    useEffect(() => {
        if (navigation.state === 'idle') {
            setPendingNavigationTarget(null);
        }
    }, [navigation.state]);

    /**
     * Core Pathname Check:
     * Verifies if we are navigating to an entirely different URL path.
     * This prevents skeletons from triggering during internal grid interactions like pagination (?page=2),
     * product sorting (?sort=...), or active facet refinements where the pathname stays identical.
     */
    const isPathnameChanging = Boolean(
        isNavigating && navigation.location && navigation.location.pathname !== location.pathname
    );

    /**
     * Master Transition Toggle:
     * Combines the asynchronous router pending state with the immediate synchronous local click state.
     * This eliminates the "phantom frame" effect where the UI would flash or layout-shift for a single
     * frame before the router transition actively kicked in.
     */
    const isCategoryChanging = pendingNavigationTarget !== null || isPathnameChanging;

    // Instantly retrieve child data from memory if the user clicked a direct child tag
    const pendingDirectChild = useMemo(
        () => (pendingNavigationTarget ? findDirectChildCategory(category, pendingNavigationTarget.id) : undefined),
        [category, pendingNavigationTarget]
    );

    // Safe type-narrowed resolution of the target category node along the current taxonomy tree
    const pendingRouteCategory = useMemo(() => {
        if (!isPathnameChanging || !navigation.location) {
            return undefined;
        }
        return resolveCategoryNodeForPlpPathname(category, navigation.location.pathname);
    }, [category, isPathnameChanging, navigation.location]);

    // Choose the best available optimistic source, falling back to the current active category
    const optimisticCategory = pendingDirectChild ?? pendingRouteCategory?.node;
    const displayCategory = optimisticCategory ?? category;

    // Structural tree classification helpers
    const isAncestor = pendingRouteCategory?.source === 'ancestor';

    /** Lateral navigation occurs when changing categories horizontally (e.g., leaf sibling to leaf sibling) */
    const isLateralNavigation = Boolean(isPathnameChanging && !pendingRouteCategory);

    const pendingNavigationTitle = pendingNavigationTarget?.name?.trim();

    // Derive localized copy text (title and description) from the target display node
    const { title: categoryTitle, description } = useMemo(
        () => plpHeaderDisplayForCategoryNode(displayCategory),
        [displayCategory]
    );

    // Sibling tags already expose the destination name; use it until the route loader resolves the full node.
    const title = pendingNavigationTitle && !optimisticCategory ? pendingNavigationTitle : categoryTitle;

    /** * Skeleton Guard Rules:
     * - Title: skeleton only on lateral jumps with no tag-provided name (e.g. breadcrumb navigation).
     * - Description: Skeletonized if we lack an optimistic node, are moving up to an ancestor, or jumping laterally.
     * - Subcategories: Skeletonized if the target node is an ancestor or a leaf category (requiring server-side sibling resolution).
     */

    const showDescriptionSkeleton = isCategoryChanging && (!optimisticCategory || isAncestor || isLateralNavigation);

    const showSubcategoriesSkeleton =
        isCategoryChanging && (!optimisticCategory || !hasPlpSubcategoryTags(optimisticCategory) || isAncestor);

    if (!PLP_HEADER_OPTIMISM_ENABLED) {
        const currentCategoryDisplay = plpHeaderDisplayForCategoryNode(category);

        return {
            displayCategory: category,
            ...currentCategoryDisplay,
            showDescriptionSkeleton: false,
            showSubcategoriesSkeleton: false,
            isNavigating,
            setPendingNavigationTarget: () => undefined,
        };
    }

    return {
        displayCategory,
        title,
        description,
        showDescriptionSkeleton,
        showSubcategoriesSkeleton,
        isNavigating,
        setPendingNavigationTarget,
    };
}
